import { useTranslation } from 'react-i18next';

/**
 * REC-04 / STA-04 — In-app notificação genérica para o denunciante.
 *
 * Renderiza apenas quando `unseen === true`. O conteúdo é deliberadamente
 * **genérico** ("Nova mensagem disponível"): NÃO exibe caseId, timestamp
 * preciso, origem da mensagem ou qualquer metadado que possa rastrear o
 * denunciante. A categorização visual mais detalhada acontece dentro do
 * StatusPage, mensagem por mensagem (ver `categorizeMessage` em
 * lib/status-decrypt.ts).
 *
 * a11y: usa `role="status"` + `aria-live="polite"` (WCAG 4.1.3) para que
 * leitores de tela anunciem a chegada da nova mensagem sem interromper o
 * usuário, paridade com o pattern já usado em `.alert` no global.css.
 */
interface NotificacaoBadgeProps {
  /** STA-04 flag — vem direto do response /api/status/lookup. */
  unseen: boolean;
  /** Disparado quando o denunciante clica em "Ver mensagens".
   *  Convenção: o caller marca o flag como visto via POST /api/status/messages
   *  (handler `markPhaseChangeSeen` no Worker). */
  onAcknowledge: () => void;
}

export function NotificacaoBadge({
  unseen,
  onAcknowledge,
}: NotificacaoBadgeProps): JSX.Element | null {
  const { t } = useTranslation();

  if (!unseen) {
    // Nada a anunciar — render null mantém o DOM enxuto e o leitor de tela
    // silencioso. O role=status é re-criado quando `unseen` flipa para true.
    return null;
  }

  return (
    <div className="notif-badge" role="status" aria-live="polite">
      <span>{t('status.nova_mensagem')}</span>
      <button type="button" className="notif-badge-action" onClick={onAcknowledge}>
        {t('status.nova_mensagem_acao')}
      </button>
    </div>
  );
}
